import FormActionButtons from '@components/FormActionButtons';
import { FormLabel, Stack, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import React from 'react';

const SOCIAL_PATTERNS = {
    website: {
        pattern: /^https?:\/\/([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/,
        message: 'URL invalide (ex: https://example.com)',
    },
    instagram: {
        pattern: /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_.]{1,30}\/?$/,
        message: 'Lien Instagram invalide (ex: https://instagram.com/username)',
    },
    facebook: {
        pattern: /^https?:\/\/(www\.)?facebook\.com\/[A-Za-z0-9._%+-]{1,50}\/?$/,
        message: 'Lien Facebook invalide (ex: https://facebook.com/page)',
    },
    linkedin: {
        pattern: /^https?:\/\/(www\.)?linkedin\.com\/(company|in)\/[A-Za-z0-9_-]{1,100}\/?$/,
        message: 'Lien LinkedIn invalide (ex: https://linkedin.com/company/name)',
    },
};

export type ContactsFormValues = {
    social: {
        website: string;
        instagram: string;
        facebook: string;
        linkedin: string;
    };
};

type CompanyContactsFormProps = {
    pro: any;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: ContactsFormValues) => Promise<void> | void;
};

const CompanyContactsForm = ({ pro, loading = false, onCancel, onSubmit }: CompanyContactsFormProps) => {
    const defaultValues = React.useMemo<ContactsFormValues>(
        () => ({
            social: {
                website: pro?.social?.website || '',
                instagram: pro?.social?.instagram || '',
                facebook: pro?.social?.facebook || '',
                linkedin: pro?.social?.linkedin || '',
            },
        }),
        [pro],
    );

    const { control, handleSubmit, formState: { isDirty, isValid } } = useForm<ContactsFormValues>({
        mode: 'all',
        defaultValues,
    });

    return (
        <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={1.25} sx={{ pt: 1 }}>
          
            <Controller
                name="social.website"
                control={control}
                rules={{
                    validate: (v) => !v || SOCIAL_PATTERNS.website.pattern.test(v) || SOCIAL_PATTERNS.website.message,
                }}
                render={({ field, fieldState }) => (
                    <>
                        <FormLabel>Site web</FormLabel>
                        <TextField {...field} size="small" disabled={loading} error={!!fieldState.error} helperText={fieldState.error?.message} />
                    </>
                )}
            />

            <Controller
                name="social.instagram"
                control={control}
                rules={{
                    validate: (v) => !v || SOCIAL_PATTERNS.instagram.pattern.test(v) || SOCIAL_PATTERNS.instagram.message,
                }}
                render={({ field, fieldState }) => (
                    <>
                        <FormLabel>Instagram</FormLabel>
                        <TextField {...field} size="small" disabled={loading} error={!!fieldState.error} helperText={fieldState.error?.message} />
                    </>
                )}
            />

            <Controller
                name="social.facebook"
                control={control}
                rules={{
                    validate: (v) => !v || SOCIAL_PATTERNS.facebook.pattern.test(v) || SOCIAL_PATTERNS.facebook.message,
                }}
                render={({ field, fieldState }) => (
                    <>
                        <FormLabel>Facebook</FormLabel>
                        <TextField {...field} size="small" disabled={loading} error={!!fieldState.error} helperText={fieldState.error?.message} />
                    </>
                )}
            />

            <Controller
                name="social.linkedin"
                control={control}
                rules={{
                    validate: (v) => !v || SOCIAL_PATTERNS.linkedin.pattern.test(v) || SOCIAL_PATTERNS.linkedin.message,
                }}
                render={({ field, fieldState }) => (
                    <>
                        <FormLabel>LinkedIn</FormLabel>
                        <TextField {...field} size="small" disabled={loading} error={!!fieldState.error} helperText={fieldState.error?.message} />
                    </>
                )}
            />

            <FormActionButtons
                onCancel={onCancel}
                loading={loading}
                submitDisabled={!isDirty || !isValid || loading}
                containerSx={{ pt: 0.5 }}
                size="small"
            />
        </Stack>
    );
};

export default CompanyContactsForm;
